import { DestroyRef, Injectable, inject, signal, computed } from '@angular/core';
import { debounceTime, filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
// eslint-disable-next-line no-restricted-imports -- LanguageService wraps TranslocoService; this is the one legitimate usage
import { TranslocoService } from '@jsverse/transloco';

export interface Language {
  code: string;
  label: string;
  nativeLabel: string;
  locale: string;
}

/**
 * Service for managing application language settings.
 *
 * Usage:
 * ```typescript
 * export class MyComponent {
 *   private languageService = inject(LanguageService);
 *
 *   // Get available languages
 *   languages = this.languageService.availableLanguages;
 *
 *   // Get current language as signal
 *   currentLang = this.languageService.currentLanguage;
 *
 *   // Switch language
 *   onLanguageChange(code: string) {
 *     this.languageService.setLanguage(code);
 *   }
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private transloco = inject(TranslocoService);
  private destroyRef = inject(DestroyRef);
  private STORAGE_KEY = 'preferredLanguage';

  /**
   * Increments when translations for the current language finish loading.
   * Used by translate()/translateObject() so computeds re-run after async scope loading.
   * Also exposed publicly for components that need to force re-computation when translations load.
   */
  readonly translationsReady = signal(0);

  constructor() {
    this.transloco.events$
      .pipe(
        filter(e => {
          if (e.type === 'langChanged') return true;
          if (e.type !== 'translationLoadSuccess') return false;
          const lang = this.transloco.getActiveLang();
          return e.payload.langName === lang || e.payload.langName.endsWith('/' + lang);
        }),
        debounceTime(150), // Wait for multiple scopes (root + patients) to finish loading
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.translationsReady.update(n => n + 1);
      });
  }

  /**
   * Available languages in the application.
   */
  readonly availableLanguages: Language[] = [
    { code: 'en', label: 'English', nativeLabel: 'English', locale: 'en-US' },
    { code: 'es', label: 'Spanish', nativeLabel: 'Español', locale: 'es-ES' },
    { code: 'fr', label: 'French', nativeLabel: 'Français', locale: 'fr-FR' },
    { code: 'zh-CN', label: 'Chinese (Simplified)', nativeLabel: '简体中文', locale: 'zh-CN' },
    { code: 'zh-TW', label: 'Chinese (Traditional)', nativeLabel: '繁體中文', locale: 'zh-TW' },
    { code: 'vi', label: 'Vietnamese', nativeLabel: 'Tiếng Việt', locale: 'vi-VN' },
    { code: 'ko', label: 'Korean', nativeLabel: '한국어', locale: 'ko-KR' },
    { code: 'fil', label: 'Filipino', nativeLabel: 'Filipino', locale: 'fil-PH' },
    { code: 'ru', label: 'Russian', nativeLabel: 'Русский', locale: 'ru-RU' },
    { code: 'ht', label: 'Haitian Creole', nativeLabel: 'Kreyòl Ayisyen', locale: 'ht-HT' },
    { code: 'pt-BR', label: 'Portuguese (Brazil)', nativeLabel: 'Português (Brasil)', locale: 'pt-BR' },
    { code: 'it', label: 'Italian', nativeLabel: 'Italiano', locale: 'it-IT' },
    { code: 'pl', label: 'Polish', nativeLabel: 'Polski', locale: 'pl-PL' },
    { code: 'de', label: 'German', nativeLabel: 'Deutsch', locale: 'de-DE' },
    { code: 'hi', label: 'Hindi', nativeLabel: 'हिन्दी', locale: 'hi-IN' },
    { code: 'ja', label: 'Japanese', nativeLabel: '日本語', locale: 'ja-JP' },
    { code: 'so', label: 'Somali', nativeLabel: 'Soomaali', locale: 'so-SO' },
    { code: 'my', label: 'Burmese', nativeLabel: 'မြန်မာဘာသာ', locale: 'my-MM' },
  ];

  /**
   * Current active language code.
   */
  readonly currentLanguage = signal(this.transloco.getActiveLang());

  /**
   * Current language object with full details.
   */
  readonly currentLanguageDetails = computed(() => {
    const code = this.currentLanguage();
    return this.availableLanguages.find(l => l.code === code) ?? this.availableLanguages[0];
  });

  /**
   * Set the active language.
   * Persists to localStorage and updates Transloco.
   */
  setLanguage(langCode: string): void {
    if (!this.availableLanguages.some(l => l.code === langCode)) {
      // eslint-disable-next-line no-console
      console.warn(`Language '${langCode}' is not supported.`);
      return;
    }

    this.transloco.setActiveLang(langCode);
    this.currentLanguage.set(langCode);

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(this.STORAGE_KEY, langCode);
    }
  }

  /**
   * Get the stored language preference from localStorage.
   */
  getStoredLanguage(): string | null {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem(this.STORAGE_KEY);
    }
    return null;
  }

  /**
   * Get the current locale string for Intl APIs (e.g., DateTimeFormat).
   *
   * @returns Locale string (e.g., 'en-US', 'es-ES', 'fr-FR')
   */
  getLocale(): string {
    const langCode = this.currentLanguage();
    const language = this.availableLanguages.find(l => l.code === langCode);
    return language?.locale || 'en-US';
  }

  /**
   * Translate a key. Use this instead of TranslocoService.translate() in class code
   * (computeds, methods, services) so translations react to language changes.
   *
   * @param key - Translation key (e.g., 'patients.list.title', 'buttons.save')
   * @param params - Interpolation params (e.g., { name: 'John' })
   * @param lang - Override language (optional)
   */
  translate<T = string>(key: string, params?: Record<string, unknown>, lang?: string): T {
    this.translationsReady(); // track signal so computeds re-run after async scope loading
    return this.transloco.translate<T>(key, params, lang);
  }

  /**
   * Translate an object of keys. Use this instead of TranslocoService.translateObject()
   * in class code so translations react to language changes.
   *
   * @param key - Translation key for the object (e.g., 'ui.select.options')
   * @param params - Interpolation params (optional)
   * @param lang - Override language (optional)
   */
  translateObject<T>(key: string, params?: Record<string, unknown>, lang?: string): T {
    this.translationsReady(); // track signal so computeds re-run after async scope loading
    return this.transloco.translateObject<T>(key, params, lang);
  }
}
